import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  ColonnadeTerminationType,
  WallIntersectionType,
  EaveType,
  FasciaTopTrimSize,
  WallRoofBaseBoardFilter,
  WallRoofBaseBoardMode,
} from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { headCasingSlicesNew } from "../settingsTools";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultExteriorDoorCasingSettings,
  defaultExteriorWindowCasingSettings,
  defaultFarmhouseWindowSettings,
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
  defaultWindowTrimSettings,
} from "./defaultSettings";

const beazerRoofSettings: RoofSettings = {
  type: SettingsType.ROOF_SETTINGS,
  eaveOverhang: 12,
  rakeOverhang: 12,
  shedOverhang: 8,
  eaveType: EaveType.ANGLED_ENCLOSED_OPEN,
  eaveReturnDistance: 4,
  eaveExtensionAtCoplanarRake: 4,
  eaveExtensionAtNonCoplanarRake: 0,
  eaveExtensionAtCoplanarWall: 1.75,
  rakeExtensionAtCoplanarWall: 0,
  includeFasciaTopTrim: true,
  fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
  eaveFasciaSize: 8,
  rakeFasciaSize: 8,
  rakeFriezeBoard: defaultFriezeBoardSettings,
  includeFriezeBoardOnPorches: true,
  roofFramingSpacing: 24,
  lookoutRafterSpacing: 24,
  includeFlyRafters: true,
  includeLookoutRafters: true,
  includeStickFramingAtStart: true,
  webChordSpacingAtStart: 16,
  includeStickFramingAtEnd: true,
  webChordSpacingAtEnd: 16,
  setBackForStepDownTruss: 72,
  generateRoofFraming: false,
};

const rearPorchSettings: RearPorchSettings = {
  type: SettingsType.REAR_PORCH_SETTINGS,
  maxColumnSpacing: 240,
  doorPathColumnSpread: 72,
  colonnadeTerminationRules: [
    {
      endType: WallIntersectionType.TEE,
      terminationType: ColonnadeTerminationType.NO_COLUMN,
    },
  ],
  includeRailing: false,
  baseColumnOffset: true,
  internalColumnStructureSize: 3.5,
  internalHeaderStructureWidth: 3.5,
  headerCasingOffsetFromCenterLine: 1.375,
  headerCasingThickness: 8.25,
  columnCasingOffset: 0,
  columnId: undefined,
};

const beazerSettings: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Beazer_A2_ACL]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Beazer_A2_ACL],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Beazer_A2_ACL + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Beazer_A2_ACL],
    },
    exteriorProps: {
      type: SettingsType.EXTERIOR_PROP_SETTINGS,
      propTypes: {
        ...defaultExteriorPropCatalog,
        garageDoorSingle: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
          propAssetId: PropId.DoorGar_S8x7_4x4Pattern_01,
        },
        garageDoorDouble: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
          propAssetId: PropId.DoorGar_D16x7_4x4Pattern_01,
        },
        doorSingleGlass: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
          propAssetId: PropId.DoorExt_Single36_Panel6_02,
        },
        doorSingleGlassLow: {
          ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
          propAssetId: PropId.DoorExt_Single36_Panel6_01,
        },
      },
    },
    roof: beazerRoofSettings,
    dormer: {
      ...defaultHomeSettings.dormer,
      eaveReturnDistance: 4,
    },
    exteriorTrim: {
      type: SettingsType.EXTERIOR_TRIM_SETTINGS,
      enableOutsideCornerMoulding: true,
      enableInsideCornerMoulding: false,
      wallRoofBaseBoard: WallRoofBaseBoardMode.NONE,
      wallRoofBaseBoardFilter: WallRoofBaseBoardFilter.ALL_EDGES,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 0,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
    },
    porch: {
      ...defaultPorchSettings,
      maxColumnSpacing: 120,
      doorPathColumnSpread: 72,
      colonnadeTerminationRules: [
        {
          endType: WallIntersectionType.TEE,
          terminationType: ColonnadeTerminationType.NO_COLUMN,
        },
      ],
      includeRailing: false,
      columnId: undefined,
    },
    rearPorch: rearPorchSettings,
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      ...headCasingSlicesNew(
        [{ height: 6, depthOffset: 1, widthOffset: 0, material: "headCasing0" }],
        true,
        0.01,
        0.5,
        0.1,
        0.1,
      ),
    },

    ...defaultFarmhouseWindowSettings,
    ...defaultWindowTrimSettings,
    exteriorWindowCasing: {
      ...defaultExteriorWindowCasingSettings,
      ...headCasingSlicesNew(
        [
          { height: 5, depthOffset: 1, widthOffset: 0, material: "trimExterior0" },
          { height: 1, depthOffset: 1.15, widthOffset: 1, material: "trimExterior0" },
          { height: 1, depthOffset: 1.25, widthOffset: 1, material: "trimExterior0" },
        ],
        false,
      ),
    },
  },
};

export default beazerSettings;
